import { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SuggestedTimesDisplay from './SuggestedTimesDisplay';
import DateTimePicker from './DateTimePicker';

interface TimeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (selectedTime?: string, newSuggestedTimes?: string[]) => void;
  existingSuggestedTimes: string[]; // Array of UTC datetime strings
  suggestedBy: 'decision' | 'reader';
  suggestedByName?: string;
  blockedTimes?: string[]; // Times already discussed that should be blocked
}

export default function TimeSelectionModal({ 
  open, 
  onClose, 
  onSubmit, 
  existingSuggestedTimes,
  suggestedBy,
  suggestedByName,
  blockedTimes = []
}: TimeSelectionModalProps) {
  const { t } = useTranslation();
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showAlternativeTimes, setShowAlternativeTimes] = useState(false);
  const [newSuggestedTimes, setNewSuggestedTimes] = useState<string[]>([]);

  // Calculate how many unique dates have been selected for new times
  const newSelectedDatesCount = new Set(newSuggestedTimes.map(time => new Date(time).toISOString().split('T')[0])).size;

  const handleSubmit = () => {
    if (!showAlternativeTimes && selectedTime) {
      // Submit the selected existing time
      onSubmit(selectedTime);
    } else if (showAlternativeTimes && newSuggestedTimes.length > 0) {
      // Submit the new suggested times
      onSubmit(undefined, newSuggestedTimes);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedTime(null);
    setNewSuggestedTimes([]);
    setShowAlternativeTimes(false);
    onClose();
  };

  const canSubmit = 
    (!showAlternativeTimes && selectedTime !== null) || 
    (showAlternativeTimes && newSelectedDatesCount >= 3);

  const getButtonText = () => {
    if (!showAlternativeTimes) {
      return selectedTime 
        ? t('time_selection.confirm_time', 'Bekräfta tid')
        : t('time_selection.select_time', 'Välj en tid');
    } else {
      if (newSelectedDatesCount === 0) {
        return t('time_selection.select_minimum', 'Välj minst 3 datum');
      } else if (newSelectedDatesCount === 1) {
        return t('time_selection.select_two_more', 'Välj 2 datum till');
      } else if (newSelectedDatesCount === 2) {
        return t('time_selection.select_one_more', 'Välj 1 datum till');
      } else {
        return t('time_selection.suggest_times', 'Föreslå tider');
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          handleClose(); // Just close, don't submit
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgb(56,25,30)',
          borderRadius: 3,
          color: 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        pb: 2
      }}>
        {t('time_selection.modal_title', 'Koordinera tid för träff')}
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pb: 2 }}>
        {!showAlternativeTimes ? (
          <>
            {/* Show existing suggested times */}
            {existingSuggestedTimes && existingSuggestedTimes.length > 0 && (
              <SuggestedTimesDisplay
                suggestedTimes={existingSuggestedTimes}
                suggestedBy={suggestedBy}
                suggestedByName={suggestedByName}
                mode="select"
                onSelectTime={setSelectedTime}
                selectedTime={selectedTime}
              />
            )}

          </>
        ) : (
          <>
            {/* Show date picker for alternative times */}
            <DateTimePicker 
              suggestedTimes={newSuggestedTimes}
              onChange={setNewSuggestedTimes}
              blockedTimes={blockedTimes}
            />

          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 1, 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        '& > :not(:first-of-type)': {
          marginLeft: 0
        }
      }}>
        {/* Always show submit button, but disable when requirements not met */}
        {(selectedTime || showAlternativeTimes) && (
          <Button 
            onClick={handleSubmit}
            disabled={!canSubmit}
            variant="contained"
            sx={{ 
              bgcolor: !canSubmit ? 'rgba(142, 96, 60, 0.5)' : '#8E603C',
              color: 'white',
              fontWeight: 'bold',
              width: '200px',
              '&:hover': { 
                bgcolor: !canSubmit ? 'rgba(142, 96, 60, 0.5)' : '#76451F',
                color: 'white'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(142, 96, 60, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}
          >
            {getButtonText()}
          </Button>
        )}
        <Button 
          onClick={() => {
            if (!showAlternativeTimes) {
              // Switch to alternative times mode instead of closing
              setShowAlternativeTimes(true);
              setSelectedTime(null);
            } else {
              // If already in alternative times mode, close the modal
              handleClose();
            }
          }}
          variant="text"
          sx={{ 
            color: 'rgba(255,255,255,0.7)',
            fontSize: '0.9rem',
            textTransform: 'none',
            width: '250px',
            '&:hover': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              color: 'white'
            }
          }}
        >
          {!showAlternativeTimes 
            ? t('time_selection.suggest_alternative', 'Ingen av tiderna passar - föreslå egna tider')
            : t('common.cancel', 'Avbryt')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}